import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getInventoryItemsManage, getInventoryTransactions } from '../../utils/inventoryApi';
import type { InventoryItem, InventoryTransactionRow } from '../../types/inventory';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  immunization_admin: 'Sales/Dispensed (Immunization)',
  po_receive: 'Received (PO)',
  manual_adjust_in: 'Manual Adjustment (+)',
  manual_adjust_out: 'Manual Adjustment (-)',
};

export const Transactions: React.FC = () => {
  const { user } = useAuth();
  const practiceId = user?.practice_id;

  const [items, setItems] = useState<InventoryItem[]>([]);
  const [rows, setRows] = useState<InventoryTransactionRow[]>([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');

  const itemNameByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const it of items) {
      map.set(`${it.catalog_type}:${it.catalog_item_id}`, it.name);
    }
    return map;
  }, [items]);

  useEffect(() => {
    if (!practiceId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getInventoryItemsManage(practiceId, { include_inactive: false });
        if (!cancelled) setItems(data);
      } catch (e) {
        // Non-blocking; rows will still show IDs
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  const fetchRows = async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const data = await getInventoryTransactions(practiceId, {
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        source_type: filterType !== 'all' ? filterType : undefined,
      });
      setRows(data);
    } catch (err: any) {
      console.error('Failed to fetch inventory transactions:', err);
      alert(err?.message || 'Failed to fetch inventory transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!practiceId) return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const formatOccurredAt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="block rounded-md border-gray-300 shadow-sm text-sm px-3 py-2"
            />
            <span className="text-gray-500 text-sm">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="block rounded-md border-gray-300 shadow-sm text-sm px-3 py-2"
            />
          </div>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="block rounded-md border-gray-300 shadow-sm text-sm px-3 py-2"
          >
            <option value="all">All Transactions</option>
            <option value="immunization_admin">Sales/Dispensed</option>
            <option value="po_receive">Received (PO)</option>
            <option value="manual_adjust_in">Manual Adjustments (+)</option>
            <option value="manual_adjust_out">Manual Adjustments (-)</option>
          </select>

          <button
            onClick={fetchRows}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            Apply
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Qty</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-10 text-center text-sm text-gray-500">
                  No transactions found.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((r) => {
                const key = `${r.catalog_type}:${r.catalog_item_id}`;
                const name = itemNameByKey.get(key) || r.catalog_item_id;
                const typeLabel = SOURCE_TYPE_LABELS[r.source_type] || r.source_type;
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatOccurredAt(r.occurred_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="font-medium">{name}</div>
                      <div className="text-xs text-gray-500">{r.catalog_type}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{typeLabel}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{r.quantity}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {r.notes || '—'}
                      {r.allocations?.length ? (
                        <div className="mt-1 text-xs text-gray-500">
                          {r.allocations.map((a) => a.lot_number || a.lot_id).join(', ')}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


